import { DestroyRef, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Observable, animationFrameScheduler, auditTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { MessageService } from '../message/message.service';

@Injectable({ providedIn: 'root' })
export class ResizeReporter {
  // Injected dependencies
  private zone = inject(NgZone);
  private doc = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);
  private messageService = inject(MessageService);

  /** Have we started sending resize updates */
  private started = false;

  /**
   * Start listening for resize updates
   * @param target HTML element to send resize updates for
   */
  start(target: Element = this.doc.body): void {
    const win = this.doc.defaultView;

    // Skip during SSR, when not inside an iframe, or if already running
    if (!isPlatformBrowser(this.platformId) || !win || win.parent === win || this.started) return;
    this.started = true;

    console.log('[resize]: target', target);

    this.zone.runOutsideAngular(() => {
      const root = this.doc.documentElement;

      // Create a subscription for whenever the target is resized
      const sub = new Observable<void>((observer) => {
        const ro = new ResizeObserver(() => observer.next());
        ro.observe(target);
        return () => ro.disconnect();
      })
        .pipe(
          startWith(undefined), // send the starting size
          auditTime(0, animationFrameScheduler), // at most one message per frame
          map(() => {
            return { width: Math.ceil(target.scrollWidth), height: Math.ceil(target.scrollHeight) };
          }),
          distinctUntilChanged((a, b) => a.width === b.width && a.height === b.height),
        )
        .subscribe((size) => this.messageService.resize(size.width, size.height));

      this.destroyRef.onDestroy(() => sub.unsubscribe());
    });
  }
}
